import { useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Inbox,
  KanbanSquare,
  Target,
  Building2,
  ListChecks,
  Users,
  Activity,
  Package,
  FileText,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useDashboard } from '@/api/queries/dashboard';

const items = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Visão geral' },
  { to: '/inbox', icon: Inbox, label: 'Inbox' },
  { to: '/triage', icon: Target, label: 'Triage' },
  { to: '/pipeline', icon: KanbanSquare, label: 'Pipeline' },
  { to: '/empresas', icon: Building2, label: 'Empresas' },
  { to: '/empresas?tag=lote-1', icon: ListChecks, label: 'Lote 1' },
  { to: '/contatos', icon: Users, label: 'Contatos' },
  { to: '/eventos', icon: Activity, label: 'Eventos' },
  { to: '/templates', icon: FileText, label: 'Templates' },
  { to: '/servicos', icon: Package, label: 'Serviços' },
];

function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  return (
    <>
      {items.map(({ to, icon: Icon, label }) => {
        const hasQuery = to.includes('?');
        return (
          <NavLink
            key={to}
            to={to}
            end={to === '/dashboard'}
            onClick={onNavigate}
            className={({ isActive }) => {
              // /empresas e /empresas?tag=lote-1 compartilham pathname → desambigua pela tag
              let on = isActive;
              if (isActive && to.startsWith('/empresas')) {
                const noLote = !location.search.includes('tag=lote-1');
                on = hasQuery ? !noLote : noLote;
              }
              return cn(
                'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                on
                  ? 'bg-muted text-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              );
            }}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
            {label}
          </NavLink>
        );
      })}
    </>
  );
}

function EnrichProgress() {
  const { data } = useDashboard();
  const p = data?.enrich_progress;
  if (!p || p.total === 0) return null;
  const pct = p.total > 0 ? (p.enriquecidas / p.total) * 100 : 0;
  return (
    <div className="mt-auto rounded-md border border-border/70 bg-card p-2.5 space-y-1.5">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">enrichment</span>
        <span className="mono text-[10px] tabular text-muted-foreground">{pct.toFixed(1)}%</span>
      </div>
      <div className="h-1 rounded-full bg-muted overflow-hidden">
        <div
          className="h-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-baseline justify-between text-[10px] text-muted-foreground tabular">
        <span className="mono">{p.enriquecidas} / {p.total}</span>
        {p.pendentes > 0 && <span className="mono">{p.pendentes} fila</span>}
        {p.falhas > 0 && <span className="mono text-destructive">{p.falhas} erro</span>}
      </div>
    </div>
  );
}

export function Sidebar() {
  return (
    <aside className="hidden md:flex w-52 shrink-0 flex-col border-r border-border/70 bg-background/50 p-2 gap-0.5">
      <NavItems />
      <EnrichProgress />
    </aside>
  );
}

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
}

export function MobileSidebar({ open, onClose }: MobileSidebarProps) {
  // Fecha no Esc e trava o scroll do body enquanto o drawer está aberto
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <div className="md:hidden" aria-hidden={!open}>
      {/* backdrop */}
      <div
        onClick={onClose}
        className={cn(
          'fixed inset-0 z-40 bg-black/40 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />
      {/* painel deslizante */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navegação"
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-64 max-w-[80vw] flex-col gap-0.5 border-r border-border/70 bg-background p-2 shadow-xl transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="mb-1 flex items-center justify-between px-1.5 py-1">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 rounded-sm bg-primary" />
            <span className="text-sm font-semibold tracking-tight">CRM Prospect</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar menu"
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        <NavItems onNavigate={onClose} />
        <EnrichProgress />
      </aside>
    </div>
  );
}
